import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { AnyAction } from '@reduxjs/toolkit'
import type { CreateApiOptions } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react'
import { HYDRATE } from 'next-redux-wrapper'

import cognito from './cognito'
import { debounce } from 'lodash'
import { Project } from './project'
import { getStrings } from './strings'
import { REHYDRATE } from 'redux-persist'
import { Constants } from './utils/constants'
import { getStore, StoreStateType } from './store'
import { getApi } from './api-common'

declare const USE_PROXY: boolean | undefined

export function extractRehydrationInfo(
  action: AnyAction,
  {
    reducerPath,
  }: {
    reducerPath: string
  },
) {
  if (action.type === HYDRATE) {
    return action.payload[reducerPath]
  }
}

let q: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> | null =
  null

const debouncedLogout = debounce(
  () => {
    // @ts-expect-error logout endpoint is injected later
    getStore().dispatch(service.endpoints.logout.initiate({}, {}))
    // logout(getStore(), {})
  },
  500,
  { leading: true },
)
export const baseApiOptions = (queryArgs?: Partial<FetchBaseQueryArgs>) => {
  const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const _baseQuery = q
      ? q
      : fetchBaseQuery({
          baseUrl:
            typeof USE_PROXY !== 'undefined' && USE_PROXY
              ? '/api/proxy'
              : Project.api,
          fetchFn: async (args: Request) => {
            if (typeof USE_PROXY !== 'undefined' && USE_PROXY) {
              args.headers.append('originalurl', args.url)
            }
            const result = await fetch(args)
            const isUnauthorised = result?.status === 401
            const isConfirmEmail = args.url.includes('change-email-confirm')

            if (isUnauthorised && !isConfirmEmail) {
              debouncedLogout()
            }
            return result
          },
          prepareHeaders: async (headers, args) => {
            const { endpoint, getState } = args
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const state = getState() as StoreStateType

            Object.entries(Project.headers || {}).forEach(([key, value]) => {
              headers.set(key, value as string)
            })

            if (
              endpoint !== 'register' &&
              endpoint !== 'createConfirmEmail' &&
              endpoint !== 'createResetPassword' &&
              endpoint !== 'createResendConfirmationEmail' &&
              endpoint !== 'verifyAccount' &&
              endpoint !== 'createForgotPassword'
            ) {
              try {
                const session = await cognito.getSession()
                const jwtToken = await session!.getAccessToken()
                const testData = state?.devSettings?.testData
                if (testData) {
                  headers.set('x-is-test-request', `True`)
                }
                if (jwtToken?.getJwtToken()) {
                  headers.set(
                    'Authorization',
                    `Bearer ${jwtToken?.getJwtToken()}`,
                  )
                }
              } catch (e) {}
            }

            const alternativeToken = getApi()?.getToken?.()
            if (alternativeToken) {
              headers.set('Authorization', `Bearer ${alternativeToken}`)
            }
            if (typeof USE_PROXY !== 'undefined' && USE_PROXY) {
              headers.set('url-original', endpoint)
            }
            headers.set('Accept-Language', getStrings().getLanguage())
            if (Constants.E2E) {
              headers.set('E2E-Test', '1')
            }
            return headers
          },
          ...queryArgs,
        })
    q = _baseQuery
    return _baseQuery(args, api, extraOptions)
  }

  const res: Pick<
    CreateApiOptions<any, any, any, any>,
    | 'baseQuery'
    | 'keepUnusedDataFor'
    | 'refetchOnReconnect'
    | 'refetchOnFocus'
    | 'extractRehydrationInfo'
  > = {
    baseQuery,
    extractRehydrationInfo: (
      action: AnyAction,
      { reducerPath }: { reducerPath: string },
    ) => {
      if (action.type === HYDRATE) {
        return action.payload[reducerPath]
      }
    },
    keepUnusedDataFor: 10,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  }
  return res
}

const transformQuery = (query: any | undefined | null) => {
  // @ts-ignore
  if (!query?.data) {
    // if the query had data, treat it as cache
    return null
  }
  return {
    ...query,
    fulfilledTimeStamp: null,
    status: 'fulfilled',
  }
}
export const service = createApi({
  ...baseApiOptions(),
  endpoints: () => ({}),
  extractRehydrationInfo(
    action: AnyAction,
    { reducerPath }: { reducerPath: string },
  ) {
    if (
      (action.type === REHYDRATE ||
        action.type === '__NEXT_REDUX_WRAPPER_HYDRATE__') &&
      action.payload?.[reducerPath]
    ) {
      const queries: Record<string, any> = {}
      const payloadQueries = action.payload[reducerPath]?.queries as
        | Record<string, any>
        | undefined
      if (payloadQueries) {
        Object.keys(payloadQueries).forEach((key) => {
          const endpointName = key.replace(/\(.*/, '')
          // Skip cache entries for endpoints not yet registered to
          // avoid crashes when rehydration fires before all services load
          if (
            service.endpoints[endpointName as keyof typeof service.endpoints]
          ) {
            queries[key] = transformQuery(payloadQueries[key])
          }
        })
      }
      return {
        'config': {},
        'mutations': {},
        'provided': {},
        'queries': queries,
        'subscriptions': {},
      } as any
    }
  },
  keepUnusedDataFor: 10,
  reducerPath: 'service',
})
